// store/postStore.ts
import { defineStore } from 'pinia';
import api from '@/services/api';
import { useLikeStore } from './likeStore';
import { Post, createPlaceholderPost } from '@/types/Post';

async function fetchAndCachePost(
    store: any,
    PK: string,
    apiFn: (post_id: string) => Promise<{ post: Post; user_likes: string[] }>
): Promise<Post | undefined> {
    if (!store.posts) store.posts = {};

    if (!PK.startsWith('POST#')) {
        PK = `POST#${PK}`;
    }

    if (PK.startsWith('POST#PLACEHOLDER')) {
        console.log('[fetchAndCachePost] Placeholder PK, skipping:', PK);
        return undefined;
    }

    console.log('[fetchAndCachePost] Fetching from API using:', apiFn.name, 'PK:', PK);

    try {
        const resp = await apiFn(PK);
        if (resp?.post) {
            const likeStore = useLikeStore();
            store.update(PK, resp.post);
            likeStore.addUserLikes(resp.user_likes || []);
            return resp.post;
        } else {
            console.warn('[fetchAndCachePost] No post returned from API:', PK);
        }
    } catch (err) {
        console.error('[fetchAndCachePost] API error:', err);
    }

    return undefined;
}

interface PostStoreState {
    posts: { [PK: string]: Post };
    uploadProgressByPostId: Record<string, number>;
    processingStartByPostId: Record<string, number>;
    feedPostIds: string[];
    lastEvaluatedKey: any; // Adjust type as needed
}

export const usePostStore = defineStore('postStore', {
    state: (): PostStoreState => ({
        posts: {},
        uploadProgressByPostId: {},
        processingStartByPostId: {},

        feedPostIds: [] as string[],
        lastEvaluatedKey: null as any,
    }),
    persist: true,

    actions: {
        // Add multiple posts to the store by PK
        add(post_list: Post[]) {
            if (!this.posts) this.posts = {};
            console.log('postStore post_list', post_list);
            post_list.forEach((post) => {
                if (post.PK) {
                    if (!post.PK.startsWith('POST#')) {
                        post.PK = `POST#${post.PK}`;
                    }
                    console.log('postStore Adding post to store:', post.PK);
                    this.posts[post.PK] = post;
                }
            });
        },

        get: async function (PK: string, force = false): Promise<Post | undefined> {
            if (!this.posts) this.posts = {};
            if (!PK.startsWith('POST#')) PK = `POST#${PK}`;
            if (!force && this.posts[PK]) return this.posts[PK];
            return await fetchAndCachePost(this, PK, api.getPost);
        },

        get_user_post: async function (PK: string, force = false): Promise<Post | undefined> {
            if (!this.posts) this.posts = {};
            if (!PK.startsWith('POST#')) PK = `POST#${PK}`;
            if (!force && this.posts[PK]) return this.posts[PK];
            return await fetchAndCachePost(this, PK, api.getUserPost);
        },

        async fetchInitialFeedPosts(post_id: string | null = null, limit = 50) {
            try {
                const result = await api.getRecentPosts(limit);
                const posts = result.posts || [];
                const likeStore = useLikeStore();

                // Add all fetched posts and likes
                this.add(posts);
                likeStore.addUserLikes(result.user_likes || []);
                this.lastEvaluatedKey = result.lastEvaluatedKey;

                // Normalize post IDs
                let ids = posts.map((p) => (p.PK.startsWith('POST#') ? p.PK : `POST#${p.PK}`));

                // Ensure post_id (e.g. from route) is present and moved to the top
                if (post_id) {
                    const fullId = post_id.startsWith('POST#') ? post_id : `POST#${post_id}`;

                    // This was blocking fetching a new post
                    // const inStore = !!this.posts[fullId];
                    // const inList = ids.includes(fullId);

                    // FOrcing refetch of selected post
                    const inStore = false;
                    const inList = false;

                    if (!inStore && !inList) {
                        try {
                            const fetched = await api.getPost(fullId);
                            if (fetched?.post) {
                                this.add([fetched.post]);
                                likeStore.addUserLikes(fetched.user_likes || []);
                            }
                        } catch (err) {
                            console.warn('[DEBUG] Failed to fetch post_id:', fullId, err);
                        }
                    }

                    // Move post_id to front if we now have it
                    if (this.posts[fullId]) {
                        ids = [fullId, ...ids.filter((id) => id !== fullId)];
                    } else {
                        console.warn('[DEBUG] Could not ensure post_id at top of list:', fullId);
                    }
                }

                this.feedPostIds = ids;
                console.log('[DEBUG] Final feedPostIds:', ids);
            } catch (err) {
                console.error('[postStore] Failed to fetch initial feed posts:', err);
            }
        },
        async fetchMoreFeedPosts(limit = 10) {
            if (!this.lastEvaluatedKey) return;

            try {
                const result = await api.getRecentPosts(limit, this.lastEvaluatedKey);
                this.add(result.posts);

                for (const post of result.posts) {
                    const pk = post.PK.startsWith('POST#') ? post.PK : `POST#${post.PK}`;
                    if (!this.feedPostIds.includes(pk)) {
                        this.feedPostIds.push(pk);
                    }
                }

                this.lastEvaluatedKey = result.lastEvaluatedKey;

                // Optional: cap memory
                if (this.feedPostIds.length > 30) {
                    const removed = this.feedPostIds.splice(0, this.feedPostIds.length - 30);
                    for (const pk of removed) {
                        delete this.posts[pk];
                    }
                }
            } catch (err) {
                console.error('Failed to fetch more feed posts:', err);
            }
        },

        getNextPostId(currentPK: string): string | null {
            const index = this.feedPostIds.indexOf(currentPK);
            if (index >= 0 && index < this.feedPostIds.length - 1) {
                return this.feedPostIds[index + 1];
            }
            return null;
        },

        getPrevPostId(currentPK: string): string | null {
            const index = this.feedPostIds.indexOf(currentPK);
            if (index > 0) {
                return this.feedPostIds[index - 1];
            }
            return null;
        },

        getCurrentPost(postId: string): Post | undefined {
            if (!postId.startsWith('POST#')) {
                postId = `POST#${postId}`;
            }
            return this.posts[postId];
        },

        // Remove a post by its PK
        remove(PK: string) {
            if (!this.posts) this.posts = {};
            if (!PK.startsWith('POST#')) {
                PK = `POST#${PK}`;
            }
            if (this.posts[PK]) {
                delete this.posts[PK];
            }
        },

        async publish(PK: string) {
            console.log('[postStore.publish] PK', PK);
            if (!PK.startsWith('POST#')) {
                PK = `POST#${PK}`;
            }

            const existing = this.posts[PK];
            console.log(`[postStore.publish] existing`, existing);
            if (!existing) {
                console.warn(`[postStore.publish] Post not found in store for ${PK}`);
                return;
            }

            try {
                existing.status = 'published';
                const savedPost = await api.savePost({
                    ...existing,
                    write_tags: true, // or the tag list if needed, but "true" is enough for your check
                });
                // const savedPost = await api.savePost(existing);
                console.info(`[postStore.publish]`, savedPost);
                this.posts[PK] = savedPost;
                console.info(`[postStore.publish] Successfully saved post ${PK}`);
            } catch (err) {
                console.error(`[postStore.publish] Failed to save post ${PK}`, err);
            }
        },
        // Clear the entire post cache
        clear() {
            this.posts = {};
        },

        // Update specific properties of a post
        update(PK: string, updates: Partial<Post>) {
            if (!PK.startsWith('POST#')) {
                PK = `POST#${PK}`;
            }
            const existing = this.posts[PK] || {};
            const cleaned = Object.fromEntries(
                Object.entries(updates).filter(([_, v]) => v !== '' && v !== undefined)
            );
            this.posts[PK] = {
                ...existing,
                ...cleaned,
            };
        },

        // Set upload progress for a post
        setUploadProgress(postId: string, progress: number) {
            if (!postId.startsWith('POST#')) {
                postId = `POST#${postId}`;
            }
            this.uploadProgressByPostId[postId] = progress;

            if (progress >= 100 && !this.processingStartByPostId[postId]) {
                this.processingStartByPostId[postId] = Date.now();
            }
        },

        // Get upload progress for a post
        getUploadProgress(postId: string): number {
            if (!postId.startsWith('POST#')) {
                postId = `POST#${postId}`;
            }
            return this.uploadProgressByPostId[postId] || 0;
        },

        setProcessingStart(postId: string) {
            if (!postId.startsWith('POST#')) {
                postId = `POST#${postId}`;
            }
            if (!this.processingStartByPostId[postId]) {
                this.processingStartByPostId[postId] = Date.now();
            }
        },

        getProcessingStart(postId: string): number {
            if (!postId.startsWith('POST#')) {
                postId = `POST#${postId}`;
            }
            return this.processingStartByPostId[postId] ?? 0;
        },

        clearUploadProgress(postId: string) {
            delete this.uploadProgressByPostId[postId];
            delete this.processingStartByPostId[postId];
        },
    },
});
